import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CAMINHO_AVISO_PRIVACIDADE } from '@mpa/shared/privacidade';
import { AuthProvider } from './auth/auth-context';
import {
  RequireAuth,
  RequirePermission,
  RequireRecurso,
  RequireSuperadmin,
} from './auth/guards';
import { AppLayout } from './layout/AppLayout';
import { MasterLayout } from './master/MasterLayout';
import { MASTER_ROTAS } from './master/nav';
import { Dashboard } from './pages/Dashboard';
import { Login } from './pages/Login';
import { AceitarConvite } from './pages/AceitarConvite';
import { Privacidade } from './pages/Privacidade';
import { Eventos } from './pages/Eventos';
import { EventoDetalhe } from './pages/EventoDetalhe';
import { RedirecionarConteudoDoEvento } from './pages/EventoConteudo';
import { Jornadas } from './pages/Jornadas';
import { Recomendacoes } from './pages/Recomendacoes';
import { Agendamentos } from './pages/Agendamentos';
import { Conversas } from './pages/Conversas';
import { RedirecionarParticipantes } from './pages/Participantes';
import { Perfis } from './pages/Perfis';
import { Perfil } from './pages/Perfil';
import { ImportarParticipantes } from './pages/ImportarParticipantes';
import { Configuracoes, RedirecionarConfiguracao } from './pages/Configuracoes';
import { Templates } from './pages/Templates';
import { MinhaConta } from './pages/MinhaConta';
import './index.css';
import './app.css';
import './master.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      // 401 já é tratado pelo cliente (renova e repete). Repetir aqui só
      // atrasaria a ida para o login quando a sessão realmente morreu.
      retry: (falhas, erro) => {
        const status = (erro as { status?: number }).status;
        if (status && status < 500) return false;
        return falhas < 2;
      },
    },
  },
});

const router = createBrowserRouter([
  { path: '/entrar', element: <Login /> },
  { path: '/convite', element: <AceitarConvite /> },
  // Pública porque quem abre veio do WhatsApp, no celular, ANTES de ter
  // autorizado qualquer coisa. Este é o endereço que a mensagem de
  // consentimento carrega em `{{link_privacidade}}` — daí o caminho vir da
  // constante compartilhada, e não de um literal que poderia divergir do que a
  // API manda dentro da mensagem.
  { path: CAMINHO_AVISO_PRIVACIDADE, element: <Privacidade /> },

  // Painel Master — plataforma. Shell próprio (roxo), fora do shell do cliente.
  {
    path: '/master',
    element: (
      <RequireSuperadmin>
        <MasterLayout />
      </RequireSuperadmin>
    ),
    // As rotas saem do próprio menu (`master/nav.tsx`): item com link exige
    // tela, então não existe mais item que pareça funcionar e não vá a lugar
    // nenhum. O `*` fica só para endereço digitado errado.
    children: [...MASTER_ROTAS, { path: '*', element: <Navigate to="/master" replace /> }],
  },

  {
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: '/dashboard', element: <Dashboard /> },
      { path: '/conta', element: <MinhaConta /> },

      // `/organizacoes` virou `/master/clientes`. O redirecionamento fica porque
      // o endereço antigo pode estar em favorito ou aberto numa aba.
      { path: '/organizacoes', element: <Navigate to="/master/clientes" replace /> },

      {
        path: '/eventos',
        element: (
          <RequirePermission permissoes={['events.read']}>
            <Eventos />
          </RequirePermission>
        ),
      },
      {
        path: '/eventos/:id',
        element: (
          <RequirePermission permissoes={['events.read']}>
            <EventoDetalhe />
          </RequirePermission>
        ),
      },
      {
        // O conteúdo passou a viver dentro de `/eventos/:id`. A rota fica só
        // para redirecionar quem tem o endereço antigo guardado.
        path: '/eventos/:id/conteudo',
        element: (
          <RequirePermission permissoes={['events.read']}>
            <RedirecionarConteudoDoEvento />
          </RequirePermission>
        ),
      },
      {
        // Virou aba de `/eventos/:id`. Fica só o redirecionamento.
        path: '/eventos/:id/participantes',
        element: (
          <RequirePermission permissoes={['participants.read']}>
            <RedirecionarParticipantes />
          </RequirePermission>
        ),
      },
      {
        path: '/eventos/:id/jornadas',
        element: (
          <RequirePermission permissoes={['events.read']}>
            <RequireRecurso recurso="jornadas">
              <Jornadas />
            </RequireRecurso>
          </RequirePermission>
        ),
      },
      {
        /**
         * Dentro do evento, como as jornadas, e pelo mesmo motivo: uma indicação
         * é de um evento, e "todas as recomendações do cliente" misturaria
         * públicos que nunca se encontram.
         *
         * `RequireRecurso` junto porque a etapa 8 manda apresentação por
         * WhatsApp para gente de verdade — é a mesma tranca do Ato I que vale na
         * API, e sem ela aqui a tela abriria para dar 403 em cada botão.
         */
        path: '/eventos/:id/recomendacoes',
        element: (
          <RequirePermission permissoes={['recommendations.read']}>
            <RequireRecurso recurso="jornadas">
              <Recomendacoes />
            </RequireRecurso>
          </RequirePermission>
        ),
      },
      {
        /**
         * Global, e é a única lista que pode ser.
         *
         * A regra deste código é que o que é de um evento mora dentro do evento
         * — foi por isso que Participantes virou aba e Recomendações mora em
         * `/eventos/:id/recomendacoes`. Mas o PERFIL é da pessoa, não da
         * participação (decisão A7), e a pessoa é justamente o que atravessa
         * evento. Mesmo motivo de `/agendamentos` e `/conversas`.
         *
         * `RequireRecurso` junto porque perfil só existe se jornada rodou: sem o
         * recurso liberado a tela abriria para dizer que não há nada, o que
         * confundiria "não liberado" com "ninguém conversou".
         */
        path: '/participantes',
        element: (
          <RequirePermission permissoes={['participants.read']}>
            <RequireRecurso recurso="jornadas">
              <Perfis />
            </RequireRecurso>
          </RequirePermission>
        ),
      },
      {
        // Por PARTICIPAÇÃO, não por pessoa: o cargo, a empresa e a jornada são
        // do evento, e um perfil "da pessoa" teria de escolher de qual edição
        // mostrar esses campos. A aba de cadastro linka para as outras.
        path: '/participantes/:id',
        element: (
          <RequirePermission permissoes={['participants.read']}>
            <RequireRecurso recurso="jornadas">
              <Perfil />
            </RequireRecurso>
          </RequirePermission>
        ),
      },
      {
        // Fora de `/eventos/:id` porque a pergunta é justamente a que atravessa
        // eventos. A restrição por evento continua valendo — o servidor filtra
        // pelos eventos que a pessoa alcança.
        path: '/agendamentos',
        element: (
          <RequirePermission permissoes={['events.read']}>
            <RequireRecurso recurso="jornadas">
              <Agendamentos />
            </RequireRecurso>
          </RequirePermission>
        ),
      },
      {
        path: '/eventos/:id/importar',
        element: (
          <RequirePermission permissoes={['participants.import']}>
            <ImportarParticipantes />
          </RequirePermission>
        ),
      },

      {
        // Fora de `/eventos/:id` de propósito: quem atende trabalha por fila, e
        // a fila atravessa os eventos. O evento vira filtro e etiqueta.
        path: '/conversas',
        element: (
          <RequirePermission permissoes={['conversations.read']}>
            <RequireRecurso recurso="jornadas">
              <Conversas />
            </RequireRecurso>
          </RequirePermission>
        ),
      },

      {
        /**
         * Sem `RequirePermission` na rota, de propósito.
         *
         * As quatro abas exigem permissões diferentes, e `pode()` é E, não OU:
         * listar as quatro aqui trancaria quem administra a equipe mas não mexe
         * na integração. Quem filtra é a própria tela, aba por aba, e quem não
         * alcança nenhuma vê uma explicação em vez de uma tela vazia.
         */
        path: '/configuracoes',
        element: <Configuracoes />,
      },

      // Os três endereços antigos, um por tela. Redirecionam para a aba.
      { path: '/usuarios', element: <RedirecionarConfiguracao aba="usuarios" /> },
      { path: '/auditoria', element: <RedirecionarConfiguracao aba="auditoria" /> },
      { path: '/whatsapp', element: <RedirecionarConfiguracao aba="whatsapp" /> },
      {
        path: '/templates',
        element: (
          <RequirePermission permissoes={['templates.manage']}>
            <Templates />
          </RequirePermission>
        ),
      },

      { path: '*', element: <Navigate to="/dashboard" replace /> },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
